import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from "react";

const LanguageContext = createContext(null);

const STORAGE_KEY = "kimat-language";
const DEFAULT_LANGUAGE = "en";

const SUPPORTED_LANGUAGES = [
    "en",
    "de",
    "fr",
    "es",
    "uk",
    "tr"
];

function normalizeLanguage(value) {
    const normalized = String(value || "")
        .trim()
        .toLowerCase()
        .split("-")[0];

    if (normalized === "ua") {
        return "uk";
    }

    return SUPPORTED_LANGUAGES.includes(normalized)
        ? normalized
        : DEFAULT_LANGUAGE;
}

function getInitialLanguage() {
    try {
        const storedLanguage =
            localStorage.getItem(STORAGE_KEY);

        return normalizeLanguage(storedLanguage);
    } catch {
        return DEFAULT_LANGUAGE;
    }
}

export function LanguageProvider({ children }) {
    const [language, setLanguageState] =
        useState(getInitialLanguage);

    const setLanguage = useCallback(languageCode => {
        const nextLanguage =
            normalizeLanguage(languageCode);

        setLanguageState(nextLanguage);

        try {
            localStorage.setItem(
                STORAGE_KEY,
                nextLanguage
            );
        } catch (error) {
            console.warn(
                "Sprache konnte nicht gespeichert werden:",
                error
            );
        }
    }, []);

    useEffect(() => {
        document.documentElement.lang = language;
    }, [language]);

    const value = useMemo(
        () => ({
            language,
            setLanguage,
            defaultLanguage: DEFAULT_LANGUAGE
        }),
        [language, setLanguage]
    );

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage() {
    const context = useContext(LanguageContext);

    if (!context) {
        throw new Error(
            "useLanguage muss innerhalb des LanguageProvider verwendet werden."
        );
    }

    return context;
}